export type ExpensePaymentStatus = 'unpaid' | 'partial' | 'paid';

/** Snapshot mínimo para colas de Compras (una fila por gasto ligado a OC). */
export type LinkedPurchaseExpenseRow = {
  expenseId: string;
  purchaseOrderId: string;
  paymentStatus: ExpensePaymentStatus;
  dueDate: string | null;
  amount: number;
  amountPaid: number;
  /** null = gasto sin comprobante fiscal (Libro IVA). */
  invoiceDocType: number | null;
};
export type ExpenseStatus = 'active' | 'voided';
export type ExpenseSettlementMethod = 'cash_drawer' | 'transfer' | 'card' | 'check' | 'other';
export type ExpenseSettlementStatus = 'active' | 'voided';

/** Método de pago usado en formularios de registro/liquidación de gastos. */
export type ExpenseSettleMethod = ExpenseSettlementMethod;

/** Intención de pago al crear una factura: queda a pagar o se paga ahora. */
export type ExpenseCreatePaymentIntent = 'unpaid' | 'pay_now';

/**
 * Filtro de estado de la lista de facturas.
 * `'voided'` = facturas anuladas (expense.status), el resto filtra por
 * payment_status dentro de las activas.
 */
export type ExpensePaymentStatusFilter = 'all' | ExpensePaymentStatus | 'voided';

export type ExpenseSortBy = 'expense_date_desc' | 'amount_desc' | 'amount_asc' | 'created_desc' | 'due_date_asc' | 'due_date_desc';

export type ExpenseDueStatusFilter = 'all' | 'overdue' | 'due_soon' | 'no_due_date';

export type ExpenseListQuery = {
  branchId: string;
  fromDate?: string;
  toDate?: string;
  paymentStatus?: ExpensePaymentStatusFilter;
  dueStatus?: ExpenseDueStatusFilter;
  categoryId?: string | null;
  supplierId?: string | null;
  search?: string;
  sortBy?: ExpenseSortBy;
};

export type ExpenseListSummary = {
  count: number;
  total: number;
  unpaidTotal: number;
  overdueCount: number;
  overdueTotal: number;
};

export const EXPENSES_LIST_EXPORT_MAX_ROWS = 5000;

export type ExpenseSettlementsListQuery = {
  branchId: string;
  fromIso: string;
  toIso: string;
  method?: ExpenseSettlementMethod | null;
  status?: 'active' | 'voided' | 'all';
  search?: string | null;
  limit?: number;
  offset?: number;
};

/**
 * Comprobante fiscal recibido de un proveedor (Wave 4 — Libro IVA compras).
 * Solo lo capturan orgs Responsable Inscripto; si está presente se persiste
 * atómicamente con el gasto y alimenta los TXT COMPRAS_CBTE / COMPRAS_ALICUOTAS.
 * `null`/ausente = gasto informal → no entra al libro.
 */
export type ExpenseFiscalInvoiceInput = {
  /** Tipo comprobante compras: 1=A 6=B 11=C 51=M · NC 3/8/13/53 · ND 2/7/12/52. */
  docType: number;
  pointOfSale: number;
  invoiceNumber: number;
  /** Snapshot del identificador fiscal del vendedor (CUIT en AR). */
  supplierTaxId: string;
  /** Denominación del vendedor tal como figura en el comprobante. */
  supplierName: string | null;
  /** Desglose discriminado (solo docs A/M — B/C no discriminan IVA). */
  ivaBreakdown: import('../../fiscal/domain-api').LibroIvaBreakdown | null;
  /** Percepciones sufridas por slot del diseño oficial compras. */
  perceptions: import('../../fiscal/domain-api').LibroIvaCompraPercepciones | null;
};

export type ExpenseCategoryRow = {
  id: string;
  organizationId: string;
  slug: string;
  name: string;
  active: boolean;
  sortOrder: number;
};

export type ExpenseRow = {
  id: string;
  branchId: string;
  categoryId: string;
  categorySlug?: string;
  categoryName?: string;
  supplierId: string | null;
  supplierName?: string | null;
  amount: number;
  amountPaid: number;
  expenseDate: string;
  description: string | null;
  receiptRef: string | null;
  paymentStatus: ExpensePaymentStatus;
  status: ExpenseStatus;
  purchaseOrderId: string | null;
  stockReceptionId: string | null;
  createdAt: string;
  createdBy: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  dueDate: string | null;
  // Bloque fiscal del comprobante recibido (undefined/nulls = gasto informal).
  invoiceDocType?: number | null;
  invoicePv?: number | null;
  invoiceNumber?: number | null;
  supplierTaxId?: string | null;
  /** Snapshot del vendedor en el comprobante (fallback: nombre actual del supplier). */
  invoiceSupplierName?: string | null;
  invoiceIvaBreakdown?: import('../../fiscal/domain-api').LibroIvaBreakdown | null;
  invoicePerceptions?: import('../../fiscal/domain-api').LibroIvaCompraPercepciones | null;
};

export type ExpenseAttachmentRow = {
  id: string;
  expenseId: string;
  fileName: string;
  fileUrl: string | null;
  mimeType: string;
  fileSize: number;
  createdAt: string;
  createdBy: string | null;
  syncStatus: 'pending' | 'synced';
  localUri: string | null;
};

export type ExpenseSettlementRow = {
  id: string;
  expenseId: string;
  method: ExpenseSettlementMethod;
  amount: number;
  paidAt: string;
  cashOutflowId: string | null;
  status: ExpenseSettlementStatus;
  note: string | null;
  createdBy: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
};

export type ExpenseSettlementListRow = {
  settlementId: string;
  expenseId: string;
  /** Sucursal de la factura padre — la RPC la devuelve ya derivada; las
   *  lecturas de cache la usan para scoping estricto por sucursal. */
  expenseBranchId?: string | null;
  method: ExpenseSettlementMethod;
  settlementAmount: number;
  paidAt: string;
  settlementStatus: ExpenseSettlementStatus;
  note: string | null;
  cashOutflowId: string | null;
  createdBy: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  expenseDate: string;
  expenseAmount: number;
  paymentStatus: ExpensePaymentStatus;
  expenseDescription: string | null;
  receiptRef: string | null;
  categorySlug: string;
  categoryName: string;
  supplierName: string | null;
};

export type ExpenseSettlementsListResult = {
  rows: ExpenseSettlementListRow[];
  total: number;
};

export type ExpenseSettlementsSummary = {
  count: number;
  totalPaid: number;
  /** Total de pagos activos que salieron de la caja (cashOutflowId presente). */
  cashOutflowTotal: number;
};

export type ExpensesSummary = {
  expensesTotal: number;
  cashOutflowsTotal: number;
  unpaidTotal: number;
  byCategory: Array<{ category: string; total: number }>;
  bySettlementMethod: Array<{ method: ExpenseSettlementMethod; total: number }>;
  byPaymentStatus: Array<{ status: ExpensePaymentStatus; total: number }>;
  linkedToCashSessionTotal: number;
  unlinkedToCashSessionTotal: number;
};

export type ProfitLossSummary = {
  salesTotal: number;
  expensesTotal: number;
  grossMargin: number;
};
